import {Injectable} from '@angular/core'
import {DarkMode, Theme} from '../models/preferences.models'
import {BehaviorSubject} from 'rxjs'

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private darkModeSubject = new BehaviorSubject<DarkMode>('auto')

  readonly darkMode = this.darkModeSubject.asObservable()

  setDarkModeFromStorage() {
    const mode = localStorage.getItem('darkMode')
    this.setDarkMode(this.isDarkMode(mode) ? mode : 'auto')
  }

  setDarkMode(mode: DarkMode) {
    if (mode === 'auto') {
      const media = window.matchMedia('(prefers-color-scheme: dark)')
      mode = media.matches ? 'dark' : 'light'
    }
    document.getElementById('config-root')?.setAttribute('data-bs-theme', mode)
    localStorage.setItem('darkMode', mode)
    this.darkModeSubject.next(mode)
  }

  setTheme(theme: Theme) {
    this.calculateColors('primary', theme.primaryColor)
    this.calculateColors('secondary', theme.secondaryColor)
    this.setVariable('bs-primary', 'var(--color-primary) !important;')
    this.setVariable('bs-secondary', 'var(--color-secondary) !important;')
    this.setDarkMode(theme.darkMode)
  }

  private calculateColors(inputType: string, hex: string) {
    // Convert hex to RGB first
    let r = 0, g = 0, b = 0
    if (hex.length == 4) {
      r = parseInt(`${hex[1]}${hex[1]}`, 16)
      g = parseInt(`${hex[2]}${hex[2]}`, 16)
      b = parseInt(`${hex[3]}${hex[3]}`, 16)
    } else if (hex.length == 7) {
      r = parseInt(`${hex[1]}${hex[2]}`, 16)
      g = parseInt(`${hex[3]}${hex[4]}`, 16)
      b = parseInt(`${hex[5]}${hex[6]}`, 16)
    }
    // Then to HSL
    r /= 255
    g /= 255
    b /= 255
    const min = Math.min(r, g, b)
    const max = Math.max(r, g, b)
    const delta = max - min
    let h: number, s: number, l: number
    h = delta == 0 ? 0 : max == r ? ((g - b) / delta) % 6 : max == g ? (b - r) / delta + 2 : (r - g) / delta + 4
    h = Math.round(h * 60)
    if (h < 0) {
      h += 360
    }
    l = (max + min) / 2
    s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))
    s = +(s * 100).toFixed(1)
    l = +(l * 100).toFixed(1)
    this.setVariable(`${inputType}-color-h`, h + '')
    this.setVariable(`${inputType}-color-s`, s + '%')
    this.setVariable(`${inputType}-color-l`, l + '%')
  }

  private setVariable(name: string, value: string) {
    document.documentElement.style.setProperty(`--${name}`, value)
  }

  private isDarkMode(value: string | null): value is DarkMode {
    return !!value && ['light', 'dark', 'auto'].includes(value)
  }

}
